import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Camera, Loader2, Store, ListChecks } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const eur = (v) => (v == null ? "n/d" : `${Number(v).toFixed(2)} €`);

export function ReceiptTestRead({ contestId, onDone }) {
  const inp = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    try { setRes(await api.receiptTestRead(contestId, file)); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lettura non riuscita"); }
    finally { setBusy(false); if (inp.current) inp.current.value = ""; }
  };
  const e = res?.extracted || {};
  const m = res?.store_match;
  const pct = m?.score != null ? Math.round(m.score * 100) : null;
  return (
    <div className="space-y-2" data-testid="receipt-test-read">
      <input ref={inp} type="file" accept="image/*" className="hidden" onChange={(ev) => run(ev.target.files?.[0])} data-testid="receipt-test-file" />
      <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={() => inp.current?.click()} data-testid="receipt-test-button">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} {busy ? "Lettura in corso…" : "Prova una foto"}</Button>
      {res && (
        <div className={`rounded-lg border p-3 text-xs space-y-1 ${res.would_pass ? "border-emerald-300 bg-emerald-50/60" : "border-red-200 bg-red-50/50"}`} data-testid="receipt-test-result">
          <p className={`font-semibold ${res.would_pass ? "text-emerald-700" : "text-red-700"}`} data-testid="receipt-test-verdict">{res.would_pass ? "✅ Verrebbe accettato" : "❌ Verrebbe scartato"} · {res.reason}</p>
          <p><b>{e.store_name || "Negozio n/d"}</b>{e.vat_id ? ` · P.IVA ${e.vat_id}` : ""}{(e.address || e.city) ? ` · ${[e.address, e.zip_code, e.city, e.province].filter(Boolean).join(" ")}` : ""}</p>
          <p>{e.date || "data n/d"} {e.time || ""} · Subtotale {eur(e.subtotal)} · Totale <b>{eur(e.total)}</b>{e.document_number ? ` · Doc. ${e.document_number}` : ""} · affidabilità {Math.round((e.confidence || 0) * 100)}%{res.receipt_code ? <> · Codice <span className="font-mono-code">{res.receipt_code}</span></> : ""}</p>
          {e.items?.length > 0 && <p className="text-muted-foreground">{e.items.map((i) => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ""}${i.price != null ? ` ${Number(i.price).toFixed(2)}€` : ""}`).join(", ")}</p>}
          {m && <p className="inline-flex items-center gap-1" data-testid="receipt-test-store"><Store className="h-3 w-3" /> Sede: {m.store_name ? <><b>{m.store_name}</b> ({pct}%) · {m.score >= res.threshold ? "abbinamento automatico" : res.mode === "play" ? "gioca senza sede, abbinamento da confermare" : "finirebbe \"da verificare\""}</> : "nessuna sede riconosciuta"}</p>}
          <p className="text-[11px] text-muted-foreground">Prova: nulla è stato registrato.</p>
          {res.tips?.length > 0 && <p className="text-amber-800 font-semibold" data-testid="receipt-test-tips">💡 Consiglio per la foto: {res.tips.join(" · ")}</p>}
        </div>
      )}
    </div>
  );
}

export function ReceiptOnboarding({ contest, refreshKey }) {
  const navigate = useNavigate();
  const [cl, setCl] = useState(null);
  useEffect(() => { api.receiptChecklist(contest.id).then(setCl).catch(() => {}); }, [contest.id, contest.receipt_ocr, refreshKey]);
  if (!cl || cl.received > 0) return null;
  const done = cl.steps.filter((s) => s.done).length;
  return (
    <Card className="p-5 bg-amber-50/60 border-amber-200 space-y-3" data-testid="receipt-onboarding">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-bold text-lg flex items-center gap-2 text-amber-900"><ListChecks className="h-4 w-4" /> Guida rapida: pronti per il primo scontrino?</h3>
        <span className="text-xs font-semibold text-amber-800" data-testid="receipt-onboarding-progress">{done}/{cl.steps.length} passi completati</span>
      </div>
      <ol className="grid gap-2 md:grid-cols-3">
        {cl.steps.map((s, i) => (
          <li key={s.key} className={`rounded-lg border p-3 text-xs space-y-1 ${s.done ? "border-emerald-300 bg-white" : "border-amber-300 bg-white/70"}`} data-testid={`receipt-onboarding-step-${s.key}`}>
            <p className="font-semibold flex items-center gap-1.5">{s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-amber-500" />} {i + 1}. {s.title}</p>
            <p className="text-muted-foreground">{s.detail}</p>
            {s.link && !s.done && <Button size="sm" variant="link" className="h-auto p-0 text-xs" onClick={() => navigate(s.link)} data-testid={`receipt-onboarding-link-${s.key}`}>Apri Punti Vendita →</Button>}
          </li>
        ))}
      </ol>
      <p className="text-[11px] text-amber-800">Questa guida sparisce automaticamente quando arriva il primo scontrino.</p>
    </Card>
  );
}
